import React from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { useLandpageSettings } from '../../hooks/useLandpageSettings';
import { Zap, Sun, ShieldCheck, Calculator, Package, BarChart3, PhoneCall, Menu, X, Megaphone, Sparkles, MapPin } from 'lucide-react';

export const Navbar: React.FC = () => {
  const { activeTab, setActiveTab, setIsLeadModalOpen } = useSolarStore();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { settings } = useLandpageSettings();

  return (
    <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-emerald-500/20 text-white shadow-lg">
      <div className="w-full max-w-none px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <div 
          onClick={() => setActiveTab('calculator')}
          className="flex items-center gap-3.5 cursor-pointer group select-none"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-400 flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-all duration-300">
            <Sun className="w-7 h-7 text-white animate-spin-slow" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="font-extrabold text-base md:text-lg tracking-tight bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-300 bg-clip-text text-transparent uppercase">
                Access Technologies
              </span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30 leading-none">
                HQ
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[10px] font-bold text-amber-300 tracking-[0.16em] uppercase leading-none">
                &amp; Renewable Energy
              </span>
              <span className="text-[9px] text-slate-500 shrink-0">•</span>
              <p className="text-[10px] text-slate-400 font-semibold tracking-wide flex items-center gap-0.5 shrink-0">
                <MapPin className="w-2.5 h-2.5 text-emerald-400 shrink-0" />
                {settings.address.split(',')[0]}
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1 bg-slate-800/80 p-1.5 rounded-xl border border-slate-700/60 shadow-inner">
          <button
            onClick={() => setActiveTab('calculator')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'calculator'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Calculator className="w-4 h-4" />
            Solar Calculator
          </button>
          
          <button
            onClick={() => setActiveTab('packages')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'packages'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Package className="w-4 h-4" />
            System Packages
          </button>

          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'dashboard'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Live Power Hub
          </button>

          <button
            onClick={() => setActiveTab('financing')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'financing'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Solar BNPL
          </button>

          <button
            onClick={() => setActiveTab('audit')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'audit'
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <PhoneCall className="w-4 h-4" />
            Site Audit
          </button>

          <button
            onClick={() => setActiveTab('admin')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'admin' || activeTab === 'growth' || activeTab === 'ai-tools'
                ? 'bg-amber-600 text-white shadow-md shadow-amber-600/30'
                : 'text-amber-300 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <ShieldCheck className="w-4 h-4 text-amber-300" />
            Admin Account
          </button>
        </nav>

        {/* CTA Button & Phone */}
        <div className="hidden md:flex items-center gap-4">
          <div className="text-right hidden xl:block">
            <a href={`tel:${settings.phone}`} className="text-xs font-bold text-amber-300 hover:text-white flex items-center justify-end gap-1">
              <PhoneCall className="w-3.5 h-3.5 text-amber-400" /> Phone: {settings.phone}
            </a>
            <div className="text-[11px] font-medium text-slate-400 flex items-center justify-end gap-1">
              <MapPin className="w-3 h-3 text-emerald-400" /> {settings.address.split(',')[2] || 'Umuahia'}, {settings.address.split(',')[3] || 'Abia State'}
            </div>
          </div>
          <button
            onClick={() => setIsLeadModalOpen(true, 'Book Expert Energy Audit')}
            className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold px-5 py-2.5 rounded-xl shadow-lg shadow-emerald-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0"
          >
            <PhoneCall className="w-4 h-4" />
            Book Free Audit
          </button>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg bg-slate-800 text-slate-300 hover:text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800 px-4 pt-2 pb-6 space-y-3">
          {/* Mobile Office Badge */}
          <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
            <div className="text-xs font-bold text-white flex items-center gap-1.5 leading-normal">
              <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              {settings.address}
            </div>
            <a href={`tel:${settings.phone}`} className="text-xs font-bold text-amber-300 block pt-1">
              📞 Phone: {settings.phone}
            </a>
          </div>

          <button
            onClick={() => { setActiveTab('calculator'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium ${
              activeTab === 'calculator' ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <Calculator className="w-5 h-5" /> Solar Calculator
          </button>
          <button
            onClick={() => { setActiveTab('packages'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium ${
              activeTab === 'packages' ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <Package className="w-5 h-5" /> System Packages
          </button>
          <button
            onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium ${
              activeTab === 'dashboard' ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <BarChart3 className="w-5 h-5" /> Live Power Hub
          </button>
          <button
            onClick={() => { setActiveTab('financing'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium ${
              activeTab === 'financing' ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <ShieldCheck className="w-5 h-5" /> Solar BNPL Financing
          </button>
          <button
            onClick={() => { setActiveTab('audit'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium ${
              activeTab === 'audit' ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <PhoneCall className="w-5 h-5" /> Site Audit Scheduler
          </button>
          <button
            onClick={() => { setActiveTab('admin'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-medium ${
              activeTab === 'admin' || activeTab === 'growth' || activeTab === 'ai-tools' ? 'bg-amber-600 text-white' : 'text-amber-300 hover:bg-slate-800'
            }`}
          >
            <ShieldCheck className="w-5 h-5 text-amber-300" /> Admin Account
          </button>
          <div className="pt-2">
            <button
              onClick={() => { setIsLeadModalOpen(true, 'Book Expert Energy Audit'); setMobileMenuOpen(false); }}
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white font-semibold py-3 rounded-xl shadow-lg"
            >
              <PhoneCall className="w-5 h-5" /> Book Free Audit
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
